<template>
  <div class="position-relative">
    <input type="text"
           v-model="q"
           :disabled="readOnly"
           @focus="search"
           @keyup.enter="onEnter"
           class="form-control w-100"
           :placeholder="placeholder"/>
    <SearchSvg/>
    <div class="autocomplete-dropdown bg-white w-100 shadow-sm rounded scroll-part"
         v-if="found.length > 0 && showDropdown"
         v-click-outside="clickedOutside">
      <template v-if="model == 'service'">
        <span v-for="(value, vind) in found"
              :key="`cat-${vind}`">
          <div class="service-category">{{ value.category }}</div>
        <a class="d-block pointer"
           @click="select(f)"
           v-for="(f, find) in value.services"
           :key="`found-${find}`">{{ renderTitle(f) }}</a>
        </span>
      </template>
      <template v-else>
        <a class="d-block pointer"
           @click="select(f)"
           v-for="(f, find) in found"
           :key="`found-${find}`">{{ f.title }}</a>
      </template>
    </div>
  </div>
</template>

<script setup>
import SearchSvg from '@/assets/svg-vue/header/magnifier.svg'
import { EloquentService } from "@/services/api/eloquent.api.service"
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { useStore } from "vuex"

const props = defineProps({
  // v-model only receives the picked object; declared so the bound value does not end up as an HTML attribute
  modelValue: {},
  placeholder: {
    type: String,
    default: ''
  },
  model: {
    type: String,
    required: true
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  renderTitle: {
    type: Function,
    default: (object) => object.title
  },
  autocompleteAdditionalParams: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()

const found = ref([])
const q = ref('')
const showDropdown = ref(false)

// not reactive: not used in the template
let timer = null
let searchEnabled = true
let lastRequestId = 0 // only the latest search may show its results (an older slow response is only cached)
const cachedObjects = {} // search results by clinic id and query

const superadmin = computed(() => store.state.auth.superadmin)
const clinic = computed(() => store.state.auth.clinic)
const branch = computed(() => store.state.auth.branch)

const search = () => {
  const requestId = ++lastRequestId
  if(clinic.value && clinic.value.id in cachedObjects && q.value in cachedObjects[clinic.value.id]) {
    found.value = cachedObjects[clinic.value.id][q.value]
    setTimeout(() => {
      showDropdown.value = true
    },150)
    return
  }
  EloquentService.autocomplete(
      props.model,
      q.value,
      24,
      true,
      superadmin.value,
      superadmin.value && clinic.value ? clinic.value.id : null,
      branch.value,
      props.autocompleteAdditionalParams
  ).then(res => {
    if(clinic.value) {
      if(!cachedObjects[clinic.value.id]) cachedObjects[clinic.value.id] = {}
      cachedObjects[clinic.value.id][res.data.q] = res.data.result
    }
    if(requestId !== lastRequestId) return
    found.value = res.data.result
    showDropdown.value = true
  })
}

const select = (object) => {
  lastRequestId++ // a search still running must not reopen the dropdown
  q.value = ''
  found.value = []
  showDropdown.value = false
  emit('update:modelValue', object)
  searchEnabled = false
  setTimeout(() => {
    searchEnabled = true
  }, 300)
}

const onEnter = () => {
  if(found.value.length === 1) {
    select(found.value[0])
  }
}

const clickedOutside = () => {
  showDropdown.value = false
}

watch(q, () => {
  if(!searchEnabled) return
  if(timer) {
    clearTimeout(timer)
  }
  timer = setTimeout(() => {
    search()
  }, 250)
})

onBeforeUnmount(() => {
  if(timer) clearTimeout(timer)
})
</script>

<style scoped lang="scss">
.autocomplete-search-bar {
  max-width: 300px;
  input {
    border: none;
    background: #E0E7EE;
    border-radius: 25px;
    height: 33px;
    padding-left: 25px;
    &::placeholder {
      font-style: italic;
    }
  }
  svg {
    position: absolute;
    right: 15px;
    top: 10px;
  }
}
.autocomplete-operation-material {
  border: 1px solid $gray-icon;
  border-radius: 5px;
  width: 304px;
  @media screen and (max-width: 768px) {
    width: auto;
  }
  .form-control {
    border-radius: 5px;
    height: 32px;
  }
  svg {
    display: none;
  }
}
.service-category {
  padding: 5px 15px;
  background: #ededed!important;
  color: #a6a6a6!important;
}
.autocomplete-form-control {
  svg {
    display: none;
  }
}
.scroll-part {
  max-height: 40vh;
  overflow-y: auto;
}
</style>
